import { Link, useLocation } from 'react-router-dom'
import { config } from '../../../config'
import { FooterLink } from './FooterLink'
import alternativePages from '@/data/alternativePages.json'
import { useTranslation } from '@/hooks/useTranslation'
import { getLocalizedRoute } from '@/lib/marketingRoutes'

export function Footer() {
	const { t } = useTranslation()
	const location = useLocation()
	const isFrench = location.pathname.startsWith('/fr')

	return (
		<footer className="bg-gray-50 border-t border-gray-200 py-16">
			<div className="max-w-7xl mx-auto px-6 lg:px-8">
				{/* Top Section */}
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-10">
					{/* Company Info */}
					<div>
						<Link to="/" className="flex items-center gap-2 mb-4">
							<img
								src={`${config.logo}`}
								alt={`${config.appName} logo`}
								className="h-10"
							/>
							{/* <span className="font-bold text-2xl text-gray-900">
								{config.appName}
							</span> */}
						</Link>
						<p className="text-gray-600 text-sm leading-relaxed max-w-md">
							{config.description}
						</p>
						<p className="mt-4 text-gray-600 text-sm">
							{t('footer.madeWith')}
						</p>



					</div>

					{/* Company Links */}
					<div>
						<h3 className="text-gray-900 font-semibold text-lg uppercase tracking-wide">
							{t('footer.company.title')}
						</h3>
						<ul className="mt-4 space-y-2">
							<li>
								<FooterLink
									label={t('footer.company.contact')}
									href={`mailto:${config.supportEmail}`}
								/>
							</li>
							<li>
								<FooterLink label={t('footer.company.pricing')} href={'/#pricing'} />
							</li>
							<li>
								<FooterLink
									label={t('footer.company.ourRoadmap')}
									href={'https://feedbask-roadmap.feedbask.com/'}
								/>
							</li>
							<li>
								<FooterLink
									label={t('footer.company.becomeAffiliate')}
									href={'/affiliate-program'}
								/>
							</li>
							<li>
								<FooterLink label={t('footer.company.docs')} href={'/docs'} />
							</li>
							<li>
								<FooterLink label={t('footer.company.blog')} href={'/blog'} />
							</li>
							<li>
								<FooterLink label="Team" href="/team" />
							</li>
							<li>
								<FooterLink
									label={t('footer.company.categories')}
									href={getLocalizedRoute('categoriesFeatures', isFrench)}
								/>
							</li>
							<li>
								<FooterLink label={t('footer.company.allAlternatives')} href={getLocalizedRoute('alternatives', isFrench)} />
							</li>
							<li>
								<FooterLink label={t('footer.company.allCompare')} href={getLocalizedRoute('compare', isFrench)} />
							</li>
						</ul>
					</div>

					{/* Compare */}
					<div>
						<h3 className="text-gray-900 font-semibold text-lg uppercase tracking-wide">
							<Link to={getLocalizedRoute('compare', isFrench)} className="hover:text-primary transition-colors">
								{t('footer.compare.title')}
							</Link>
						</h3>
						<ul className="mt-4 space-y-2">
							<li>
								<FooterLink label={t('footer.compare.bestCustomerSupportSoftware')} href="/compare/best-customer-support-software" />
							</li>
						</ul>
					</div>

					{/* Feature */}
					<div>
						<h3 className="text-gray-900 font-semibold text-lg uppercase tracking-wide">
							<Link to={getLocalizedRoute('categoriesFeatures', isFrench)} className="hover:text-primary transition-colors">
								{t('footer.feature.title')}
							</Link>
						</h3>
						<ul className="mt-4 space-y-2">
							<li><FooterLink label={t('footer.feature.websiteFeedbackWidget')} href={getLocalizedRoute('websiteFeedbackWidget', isFrench)} /></li>
							<li><FooterLink label={t('footer.feature.inAppFeedbackWidget')} href={getLocalizedRoute('inAppFeedbackWidget', isFrench)} /></li>
							<li><FooterLink label={t('footer.feature.featureVotingTool')} href={getLocalizedRoute('featureVotingTool', isFrench)} /></li>
							<li><FooterLink label={t('footer.feature.publicRoadmapSoftware')} href={getLocalizedRoute('publicRoadmapSoftware', isFrench)} /></li>
							<li><FooterLink label={t('footer.feature.testimonialsWidget')} href={getLocalizedRoute('testimonialsWidget', isFrench)} /></li>
							<li><FooterLink label={t('footer.feature.bugReportWidget')} href={getLocalizedRoute('bugReportWidget', isFrench)} /></li>
						</ul>
					</div>

					{/* Alternative to */}
					<div>
						<h3 className="text-gray-900 font-semibold text-lg uppercase tracking-wide">
							<Link to={getLocalizedRoute('alternatives', isFrench)} className="hover:text-primary transition-colors">
								{t('footer.alternativeTo.title')}
							</Link>
						</h3>
						<ul className="mt-4 space-y-2">
							{Object.keys(alternativePages).map((key) => {
								const toolSlug = key.replace('alternative-', '')
								const name = toolSlug.charAt(0).toUpperCase() + toolSlug.slice(1)
								return (
									<li key={key}>
										<FooterLink label={name} href={`${getLocalizedRoute('alternatives', isFrench)}/${toolSlug}`} />
									</li>
								)
							})}
						</ul>
						<h3 className="text-gray-900 font-semibold text-lg uppercase tracking-wide mt-8">
							{t('footer.alternativeTo.youMightAlsoLike')}
						</h3>
						<ul className="mt-4 space-y-2">
							<li>
								<FooterLink
									label="Refindie"
									href="https://refindie.com/"
								/>
							</li>
						</ul>
						<ul className="mt-4 space-y-2">
							<li>
								<FooterLink
									label="Rocket MVP"
									href="https://www.rocketmvp.io/"
								/>
							</li>
						</ul>
						<ul className="mt-4 space-y-2">
							<li>
								<FooterLink
									label="Murmur"
									href="https://www.murmurtts.com/"
								/>
							</li>
						</ul>
					</div>

					<div>
						<h3 className="text-gray-900 font-semibold text-lg uppercase tracking-wide">
							{t('footer.foundersTeam.title')}
						</h3>
						<ul className="mt-4 space-y-2">
							<li>
								<FooterLink
									label={t('footer.foundersTeam.tarunYadav')}
									href="https://tarun-yadav.com/"
								/>
							</li>
							<li>
								<FooterLink
									label={t('footer.foundersTeam.paulineClavelloux')}
									href="https://paulinecx.com/"
								/>
							</li>
						</ul>
					</div>
				</div>

				{/* Bottom Section */}
				<div className="mt-12 pt-8 border-t border-gray-200">
					<div className="flex flex-col md:flex-row justify-between items-center">
						<p className="text-sm text-gray-500">
							&copy; {new Date().getFullYear()} {config.appName}. {t('footer.copyright')}
						</p>
						<div className="mt-4 md:mt-0 flex items-center space-x-6">
							<ul className="flex flex-wrap justify-center gap-x-6 gap-y-2">
								<li>
									<FooterLink label={t('footer.privacy')} href="/privacy" />
								</li>
								<li>
									<FooterLink label={t('footer.terms')} href="/tos" />
								</li>
								<li>
									<FooterLink label={t('footer.legalNotice')} href="/legal" />
								</li>
								<li>
									<FooterLink label={t('footer.cookies')} href="/cookies" />
								</li>
							</ul>
							<a
								href="https://www.uneed.best/tool/feedbask"
								target="_blank"
								rel="noopener noreferrer"
								className="transition-all hover:scale-105">
								<img
									src="https://www.uneed.best/POTD1.png"
									style={{ width: '200px' }}
									alt="Uneed POTD1 Badge"
									className="h-auto"
								/>
							</a>
						</div>
					</div>
				</div>
			</div>
		</footer>
	)
}
